/**
 * @module engine/export/pdf/views
 * @summary Place captured 3D views on a sheet — one large, or several to a page.
 * @description
 *   The 3D renderer is a software RASTERISER: it paints shaded triangles, and
 *   there is no vector equivalent of a shaded triangle that would not be an
 *   order of magnitude larger and slower. So these views arrive as PNG, and they
 *   are the only raster left anywhere in the exporter. They go in lossless —
 *   JPEG on a shaded render with thin white pipework produces exactly the muddy
 *   haloes the vector rewrite existed to remove.
 *
 *   Two arrangements, because they answer different questions. **Full** gives one
 *   view the whole sheet, which is what a supplier wants when the point is "this
 *   is what it looks like". **Grid** puts four to a page, which is what they want
 *   when the point is "here it is from every side".
 *
 *   Neither is to scale, and both say so. An isometric or a perspective view has
 *   no single scale to state, and letting a reader assume otherwise on a sheet
 *   they might measure is worse than leaving the drawing off entirely.
 */
import type { jsPDF } from 'jspdf';
import type { Rect } from './sheet';

/** A captured raster view, ready to place. */
export interface SheetImage {
  /** Caption printed under the frame, e.g. `'ISOMETRIC — SOUTH-WEST'`. */
  label: string;
  /** `data:image/png;base64,…`. */
  png: string;
  wPx: number;
  hPx: number;
}

const INK = { r: 26, g: 26, b: 26 };
const RULE = { r: 200, g: 204, b: 210 };
const CAPTION_H = 5.5;

/**
 * Fit `img` inside `box` without distorting it, and report where it landed.
 * Letterboxed rather than cropped: a plant with a corner cut off reads as a
 * mistake, whereas white space reads as a margin.
 */
function letterbox(box: Rect, wPx: number, hPx: number, pad = 1.5): Rect {
  const ar = Math.max(0.01, wPx / Math.max(1, hPx));
  let w = box.w - pad * 2;
  let h = w / ar;
  if (h > box.h - pad * 2) {
    h = box.h - pad * 2;
    w = h * ar;
  }
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

/** Frame, image and caption for one view inside `box`. */
function placeOne(doc: jsPDF, box: Rect, v: SheetImage, captionSize: number): void {
  const frame: Rect = { x: box.x, y: box.y, w: box.w, h: box.h - CAPTION_H };
  doc.setLineDashPattern([], 0);
  doc.setDrawColor(RULE.r, RULE.g, RULE.b);
  doc.setLineWidth(0.2);
  doc.rect(frame.x, frame.y, frame.w, frame.h);

  const at = letterbox(frame, v.wPx, v.hPx);
  try {
    // 'NONE' keeps the PNG bit-exact. jsPDF would otherwise re-encode.
    doc.addImage(v.png, 'PNG', at.x, at.y, at.w, at.h, undefined, 'NONE');
  } catch {
    // A view that will not decode leaves an empty frame and a caption, which is
    // at least legible as "this view is missing" rather than shifting the rest.
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(captionSize);
  doc.setTextColor(INK.r, INK.g, INK.b);
  doc.text(v.label.toUpperCase(), box.x + box.w / 2, box.y + box.h - 1, { align: 'center' });
}

/** One view, filling the sheet. */
export function drawViewFull(doc: jsPDF, area: Rect, v: SheetImage): void {
  placeOne(doc, area, v, 8);
}

/** Several views to a sheet: two columns up to four, three beyond that. */
export function drawViewGrid(doc: jsPDF, area: Rect, views: SheetImage[]): void {
  if (!views.length) return;
  const cols = views.length === 1 ? 1 : views.length <= 4 ? 2 : 3;
  const rows = Math.ceil(views.length / cols);
  const gap = 5;
  const cellW = (area.w - gap * (cols - 1)) / cols;
  const cellH = (area.h - gap * (rows - 1)) / rows;

  views.forEach((v, i) => {
    placeOne(
      doc,
      {
        x: area.x + (i % cols) * (cellW + gap),
        y: area.y + Math.floor(i / cols) * (cellH + gap),
        w: cellW,
        h: cellH,
      },
      v,
      6.8,
    );
  });
}

/** How many sheets `n` views need in each arrangement. */
export const VIEWS_PER_GRID_SHEET = 4;
export function viewSheetCount(n: number, layout: 'full' | 'grid'): number {
  if (n <= 0) return 0;
  return layout === 'full' ? n : Math.ceil(n / VIEWS_PER_GRID_SHEET);
}
